import React, { useEffect, useState, useCallback, useRef } from "react";
import baseUrl from "../../../api/baseUrl";
import AppointmentCounts from "../../appointment/viewappointment/components/AppointmentCounts";
import AppointmentFilter from "../../appointment/viewappointment/components/AppointmentFilter";
import AppointmentTr from "../../appointment/viewappointment/components/AppointmentTr";
import AppointmentTableHead from "../../appointment/viewappointment/components/AppointmentTableHead";
import AppointmentMobileCard from "../../appointment/viewappointment/components/AppointmentMobileCard";
import AppointmentDetailsModal from "../../appointment/viewappointment/components/AppointmentDetailsModal";
import TabBar from "../../../components/tabbar/TabBar";
import BottomTab from "../../../components/pagination/BottomTab";
import { useNavigate } from "react-router-dom";

const LIMIT = 20;
const MemoizedAppointmentTr = React.memo(AppointmentTr);

function MyAppointment() {
  const navigate = useNavigate();
  const [detailsItem, setDetailsItem] = useState<any | null>(null);
  const [appointment, setAppointment] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState({
    appointments: 0,
    cancelled: 0,
    completed: 0,
    pending: 0,
    inqueue: 0,
    reschudle: 0,
    ongoing: 0,
    noshow: 0,
  });
  const [all, setAll] = useState("all");
  const [selectedOption, setSelectedOption] = useState<string>("pending");
  const pageRef = useRef(1);
  pageRef.current = page;

  const getAppointments = useCallback(
    (status: any, dayFilter: string, pageNum: number = 1) => {
      baseUrl
        .get("/appointment/findAllAppointmentDentistId", {
          headers: {
            status: status,
            day: dayFilter,
            page: pageNum,
            limit: LIMIT,
          },
        })
        .then((response) => {
          setAppointment(response.data.data || []);
          setTotalCount(response.data.count ?? 0);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    []
  );

  const handleOptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSelectedOption(value);
      setPage(1);
      getAppointments(value, all, 1);
    },
    [all, getAppointments]
  );

  const handleDay = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setAll(value);
      setPage(1);
      getAppointments(selectedOption, value, 1);
    },
    [selectedOption, getAppointments]
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      getAppointments(selectedOption, all, newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [selectedOption, all, getAppointments]
  );

  const appointmentCount = useCallback(() => {
    baseUrl
      .get("/appointment/appointmentscountsbyId", {
        headers: { status: true },
      })
      .then((response) => {
        setCount({
          appointments: response.data?.appointments,
          cancelled: response.data?.cancelled,
          completed: response.data?.completed,
          pending: response.data?.pending,
          inqueue: response.data?.inqueue,
          reschudle: response.data?.reschudle,
          noshow: response.data?.noshow,
          ongoing: response.data?.ongoing,
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const appointmStatus = useCallback(() => {
    getAppointments(selectedOption, all, pageRef.current);
    appointmentCount();
  }, [getAppointments, appointmentCount, selectedOption, all]);

  useEffect(() => {
    appointmStatus();
  }, [appointmStatus]);

  return (
    <>
      <TabBar title="My Appointments" />
      <div className="mt-6 space-y-4">
        <AppointmentCounts count={count} />
        <div className="bg-white dark:bg-slate-800 rounded-xl md:rounded-2xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-4 md:p-6">
          <AppointmentFilter
            selectedOption={selectedOption}
            all={all}
            handleOptionChange={handleOptionChange}
            handleDay={handleDay}
          />

          {/* Desktop Table */}
          <div className="hidden md:block overflow-hidden rounded-xl border border-slate-200 dark:border-slate-600">
            <table className="w-full">
              <AppointmentTableHead />
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-600">
                {appointment.map((item: any, index: number) => (
                  <MemoizedAppointmentTr
                    appointmStatus={appointmStatus}
                    key={item?._id ?? index}
                    item={item}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {appointment.map((item: any, index: number) => (
              <AppointmentMobileCard
                key={item?._id ?? index}
                item={item}
                onOpenDetails={() => setDetailsItem(item)}
                onStatusChange={appointmStatus}
                onAddTreatment={(patientId) => {
                  baseUrl
                    .post("treatment/create-treatment", { patientId })
                    .then((response) => {
                      navigate(
                        `/client/treatment-diagnose/add-treatment/diagnose/${response.data.data._id}`
                      );
                    })
                    .catch((error) => console.error(error));
                }}
              />
            ))}
            {appointment.length === 0 && (
              <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                <p className="text-sm">No appointments found</p>
              </div>
            )}
          </div>

          {totalCount > LIMIT && (
            <BottomTab
              title="Appointments"
              count={totalCount}
              onPageChange={onPageChange}
            />
          )}
        </div>
      </div>

      <AppointmentDetailsModal
        item={detailsItem ?? {}}
        open={detailsItem != null}
        onClose={() => setDetailsItem(null)}
        onStatusChange={appointmStatus}
      />
    </>
  );
}

export default React.memo(MyAppointment);
